import type { ResolvedResourceGroup } from '@internal/plugin-azure-resources-common-react';

import { resourceGroupArmId } from './recentTagFixes';
import {
  findExistingTagValue,
  hasExactTagValue,
  isAzureManagedResourceGroup,
} from './resourceGroupTags';

/** A resource group the scorecard reports as failing the check. */
export type FailingResourceGroup = {
  name: string;
  /** Where the scorecard found it; older payloads carried the bare name only. */
  subscriptionId?: string;
  subscriptionName?: string;
};

export type ResourceGroupCandidate = {
  /** Full ARM id — the write target. */
  id: string;
  subscriptionId: string;
  location: string;
  existingValue?: string;
  /** Already tagged exactly as the scorecard reads it, so it passes on refresh. */
  tagged: boolean;
  ownedByTeam: boolean;
};

export type ResourceGroupRow = {
  /** Identifies the row: the ARM id when the subscription is known, else the name. */
  key: string;
  /** The failing resource group name, exactly as the scorecard reports it. */
  name: string;
  /** Subscription the scorecard reported the resource group in. */
  subscriptionId?: string;
  subscriptionName?: string;
  managed: boolean;
  /** Resource groups in Azure this row may refer to; empty when none is readable. */
  candidates: ResourceGroupCandidate[];
  /** The resource group written to; absent when none or several match. */
  target?: ResourceGroupCandidate;
  /** A bare name matched several resource groups, so there is nothing to write to. */
  ambiguous: boolean;
};

const sameId = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/**
 * Builds exactly one row per resource group reported by the scorecard, so the
 * modal always mirrors the failing check.
 *
 * The scorecard reports each resource group with its subscription, which pins it
 * to a single resource group in Azure. Older payloads carried only the name, and
 * names repeat across subscriptions (`NetworkWatcherRG` exists in most of them):
 * matches inside the team's own subscriptions win, and a name still matching
 * several resource groups is left alone rather than guessed.
 */
export const buildResourceGroupRows = ({
  resourceGroups,
  resolved,
  teamSubscriptionIds,
  tagKey,
}: {
  resourceGroups: FailingResourceGroup[];
  resolved: ResolvedResourceGroup[];
  teamSubscriptionIds: string[];
  tagKey: string;
}): ResourceGroupRow[] => {
  const teamSubscriptions = new Set(teamSubscriptionIds);

  const byName = new Map<string, ResolvedResourceGroup[]>();
  // Resolution runs in several passes, so the same resource group can arrive
  // twice; ARM ids are globally unique, which makes them the identity to
  // collapse on.
  const seenIds = new Set<string>();

  for (const resourceGroup of resolved) {
    if (seenIds.has(resourceGroup.id)) continue;
    seenIds.add(resourceGroup.id);

    const key = resourceGroup.name.toLowerCase();
    const matches = byName.get(key);
    if (matches) {
      matches.push(resourceGroup);
    } else {
      byName.set(key, [resourceGroup]);
    }
  }

  const seenKeys = new Set<string>();

  return resourceGroups.flatMap(
    ({ name, subscriptionId, subscriptionName }) => {
      const key = subscriptionId
        ? resourceGroupArmId(subscriptionId, name)
        : name.toLowerCase();
      if (seenKeys.has(key)) return [];
      seenKeys.add(key);

      const matches = byName.get(name.toLowerCase()) ?? [];
      let pool: ResolvedResourceGroup[];
      if (subscriptionId) {
        pool = matches.filter(match =>
          sameId(match.subscriptionId, subscriptionId),
        );
      } else {
        const owned = matches.filter(match =>
          teamSubscriptions.has(match.subscriptionId),
        );
        pool = owned.length ? owned : matches;
      }

      const candidates: ResourceGroupCandidate[] = pool
        .map(match => ({
          id: match.id,
          subscriptionId: match.subscriptionId,
          location: match.location,
          existingValue: findExistingTagValue(match.tags, tagKey),
          tagged: hasExactTagValue(match.tags, tagKey),
          ownedByTeam: teamSubscriptions.has(match.subscriptionId),
        }))
        .sort((a, b) => a.subscriptionId.localeCompare(b.subscriptionId));

      const ambiguous = candidates.length > 1;

      return [
        {
          key,
          name,
          subscriptionId,
          subscriptionName,
          managed: isAzureManagedResourceGroup(name),
          candidates,
          target: ambiguous ? undefined : candidates[0],
          ambiguous,
        },
      ];
    },
  );
};
